import { createAsyncThunk } from '@reduxjs/toolkit'
import { firestoreService } from '@/services/firebase/firestore.service'
import { PaymentConcept, PaymentsOptions } from '@/types/payment.types'

// Thunk to fetch document tags from general/tags
export const fetchDocumentTags = createAsyncThunk(
  'options/fetchDocumentTags',
  async (_, { rejectWithValue }) => {
    try {
      const doc = await firestoreService.getDocument('options', 'documents')
      if (!doc || !doc.tags) {
        return []
      }
      // The tags field contains an array of strings
      return doc.tags as string[]
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch document tags')
    }
  }
)

// Thunk to fetch content tags from news_events/news_tags
export const fetchContentTags = createAsyncThunk(
  'options/fetchContentTags',
  async (_, { rejectWithValue }) => {
    try {
      const doc = await firestoreService.getDocument('options','news_events')
      if (!doc || !doc.news_tags) {
        return []
      }
      // The tags field contains an array of strings
      return doc.news_tags as string[]
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch content tags')
    }
  }
)

// Thunk to fetch managers from news_events/events_managers
export const fetchManagers = createAsyncThunk(
  'options/fetchManagers',
  async (_, { rejectWithValue }) => {
    try {
      const doc = await firestoreService.getDocument('options','news_events')
      if (!doc || !doc.events_managers) {
        return []
      }
      // The managers field contains an array of strings
      return doc.events_managers as string[]
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch managers')
    }
  }
)

// Thunk to fetch all options at once
export const fetchAllOptions = createAsyncThunk(
  'options/fetchAllOptions',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const results = await Promise.allSettled([
        dispatch(fetchDocumentTags()).unwrap(),
        dispatch(fetchContentTags()).unwrap(),
        dispatch(fetchManagers()).unwrap(),
      ])

      const errors = results
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map(result => result.reason)

      if (errors.length > 0) {
        console.warn('Some options failed to load:', errors)
      }

      return {
        documentTags: results[0].status === 'fulfilled' ? results[0].value : [],
        contentTags: results[1].status === 'fulfilled' ? results[1].value : [],
        managers: results[2].status === 'fulfilled' ? results[2].value : [],
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch all options')
    }
  }
)

// Payment Concepts Management
const PAYMENT_COLLECTION = 'options';
const PAYMENT_DOCUMENT = 'payments';

export const fetchPaymentItems = createAsyncThunk(
  'options/fetchPaymentItems',
  async (_, { rejectWithValue }) => {
    try {
      const docs = await firestoreService.getDocument(PAYMENT_COLLECTION, PAYMENT_DOCUMENT) as PaymentsOptions
      docs.concepts.forEach((concept) => {
        concept.cant = 1
      })
      return docs.concepts
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch payment items')
    }
  }
)

export const fetchUpdatePurchaseConcept = createAsyncThunk(
  'options/fetchUpdatePurchaseConcept',
  async (payload: PaymentConcept, { rejectWithValue }) => {
    try {
      const docData = await firestoreService.getDocument(
        PAYMENT_COLLECTION,
        PAYMENT_DOCUMENT
      )

      if (!docData || !docData.concepts) {
        throw new Error("Document structure is invalid or 'concepts' array not found.");
      }

      const updatedConcepts = docData.concepts.map((concept: PaymentConcept) => {
        if (concept.id === payload.id) {
          return { ...payload };
        }
        return concept;
      });

      await firestoreService.updateDocument(PAYMENT_COLLECTION, PAYMENT_DOCUMENT, {
        concepts: updatedConcepts
      });

      return payload;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update purchase concept')
    }
  }
)

export const fetchDeletePurchaseConcept = createAsyncThunk(
  'options/fetchDeletePurchaseConcept',
  async (id: string, { rejectWithValue }) => {
    try {
      const docData = await firestoreService.getDocument(
        PAYMENT_COLLECTION,
        PAYMENT_DOCUMENT
      );

      if (!docData || !docData.concepts) {
        throw new Error("Document structure is invalid or 'concepts' array not found.");
      }

      const updatedConcepts = docData.concepts.filter(
        (concept: PaymentConcept) => concept.id !== id
      );

      await firestoreService.updateDocument(PAYMENT_COLLECTION, PAYMENT_DOCUMENT, {
        concepts: updatedConcepts
      });

      return id;
    } catch (error: any) {
      console.error("Error al eliminar el concepto anidado:", error);
      return rejectWithValue(error.message || 'Failed to delete purchase concept');
    }
  }
);

export const fetchAddPurchaseConcept = createAsyncThunk(
  'options/fetchAddPurchaseConcept',
  async (payload: Omit<PaymentConcept, 'id'>, { rejectWithValue }) => {
    try {
      const newId = crypto.randomUUID();
      const newConcept: PaymentConcept = { ...payload, id: newId };

      const docData = await firestoreService.getDocument(
        PAYMENT_COLLECTION,
        PAYMENT_DOCUMENT
      );

      const updatedConcepts = [...docData.concepts, newConcept];

      await firestoreService.updateDocument(PAYMENT_COLLECTION, PAYMENT_DOCUMENT, {
        concepts: updatedConcepts
      });

      return newConcept;
    } catch (error: any) {
      console.error("Error al añadir el concepto anidado:", error);
      return rejectWithValue(error.message || 'Failed to add purchase concept')
    }
  }
);

// Payment Instructions Visibility Management
const SETTINGS_COLLECTION = 'options';
const SETTINGS_DOCUMENT = 'settings';

export const fetchShowPaymentInstructions = createAsyncThunk(
  'options/fetchShowPaymentInstructions',
  async (_, { rejectWithValue }) => {
    try {
      const doc = await firestoreService.getDocument(SETTINGS_COLLECTION, SETTINGS_DOCUMENT);
      if (!doc) {
        return false;
      }
      return doc.showPaymentInstructions ?? false;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch payment instructions visibility');
    }
  }
);

export const updateShowPaymentInstructions = createAsyncThunk(
  'options/updateShowPaymentInstructions',
  async (show: boolean, { rejectWithValue }) => {
    try {
      await firestoreService.updateDocument(SETTINGS_COLLECTION, SETTINGS_DOCUMENT, {
        showPaymentInstructions: show
      });
      return show;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update payment instructions visibility');
    }
  }
);
